import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import {
  Mail, Plus, Copy, Trash2, RefreshCw, Search, Sparkles,
  CheckCircle2, AlertCircle, X, Loader2, Instagram, ExternalLink
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const EmailAliasesPanel = () => {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jgmpro/email-aliases`);
      setAliases(res.data.aliases || []);
    } catch (err) {
      console.error('Aliases load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCopy = async (email, id) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este alias? Solo borra el registro, no afecta el forwarding configurado en ImprovMX.')) return;
    try {
      await axios.delete(`${API}/jgmpro/email-aliases/${id}`);
      await load();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const handleToggleStatus = async (alias) => {
    const newStatus = alias.status === 'active' ? 'burned' : 'active';
    try {
      await axios.put(`${API}/jgmpro/email-aliases/${alias.id}`, { status: newStatus });
      await load();
    } catch (err) {
      alert('Error al actualizar estado');
    }
  };

  const filtered = aliases.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        a.email.toLowerCase().includes(q) ||
        (a.ig_username || '').toLowerCase().includes(q) ||
        (a.notes || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const stats = {
    total: aliases.length,
    active: aliases.filter(a => a.status === 'active').length,
    burned: aliases.filter(a => a.status === 'burned').length,
  };

  return (
    <div className="space-y-6" data-testid="email-aliases-panel">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
        <div className="p-2 rounded-lg bg-blue-500/20 flex-shrink-0">
          <Mail className="w-5 h-5 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Buzón de emails para registros IG</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Crea direcciones únicas <code className="px-1 py-0.5 rounded bg-white/5 text-pink-300">cualquier@jgmodels.com</code> sin configurar nada. Todo llega a tu Gmail principal vía ImprovMX. IG las ve como emails únicos.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-3 flex-wrap">
          <div className="px-4 py-2 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Total</div>
            <div className="text-xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <div className="text-[10px] uppercase tracking-wider text-emerald-400">Activos</div>
            <div className="text-xl font-bold text-emerald-300">{stats.active}</div>
          </div>
          <div className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/30">
            <div className="text-[10px] uppercase tracking-wider text-red-400">Quemados</div>
            <div className="text-xl font-bold text-red-300">{stats.burned}</div>
          </div>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          data-testid="email-aliases-add-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white font-medium shadow-lg shadow-blue-500/20 text-sm"
        >
          <Plus className="w-4 h-4" />
          Crear email
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por email, IG username o nota..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="email-aliases-search"
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-blue-400/50"
          />
        </div>
        <div className="flex p-1 rounded-lg bg-white/5 border border-white/10">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'active', label: 'Activos' },
            { id: 'burned', label: 'Quemados' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                statusFilter === f.id ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
          title="Recargar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center rounded-2xl border border-white/10 border-dashed bg-white/[0.02]">
          <Mail className="w-12 h-12 mx-auto text-gray-500 mb-3" />
          <p className="text-gray-400">{aliases.length === 0 ? 'Aún no has creado emails' : 'Sin resultados'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(a => (
            <div
              key={a.id}
              data-testid={`email-alias-${a.local}`}
              className={`group flex items-center gap-3 p-3 rounded-xl border transition-all ${
                a.status === 'burned'
                  ? 'bg-red-500/[0.03] border-red-500/20 opacity-60'
                  : 'bg-white/[0.02] border-white/10 hover:bg-white/[0.05] hover:border-white/20'
              }`}
            >
              <div className={`p-2 rounded-lg flex-shrink-0 ${a.status === 'burned' ? 'bg-red-500/10' : 'bg-blue-500/10'}`}>
                <Mail className={`w-4 h-4 ${a.status === 'burned' ? 'text-red-400' : 'text-blue-400'}`} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-mono text-sm text-white truncate">{a.email}</code>
                  {a.status === 'burned' && <span className="px-1.5 py-0.5 text-[10px] rounded bg-red-500/20 text-red-300">Quemado</span>}
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                  {a.ig_username && (
                    <a
                      href={`https://instagram.com/${a.ig_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-pink-400 hover:text-pink-300"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Instagram className="w-3 h-3" /> @{a.ig_username}
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                  {a.notes && <span className="truncate max-w-md">{a.notes}</span>}
                  <span>· {formatDate(a.created_at)}</span>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleCopy(a.email, a.id)}
                  data-testid={`email-alias-copy-${a.local}`}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
                  title="Copiar email"
                >
                  {copiedId === a.id ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleToggleStatus(a)}
                  className="px-2 py-1 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors text-xs"
                  title={a.status === 'active' ? 'Marcar como quemado' : 'Restaurar'}
                >
                  {a.status === 'active' ? '🔥' : '✓'}
                </button>
                <button
                  onClick={() => handleDelete(a.id)}
                  className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                  title="Eliminar"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddAliasModal onClose={() => setShowAddModal(false)} onCreated={() => { setShowAddModal(false); load(); }} />
      )}
    </div>
  );
};

const AddAliasModal = ({ onClose, onCreated }) => {
  const [local, setLocal] = useState('');
  const [igUsername, setIgUsername] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async (style) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/jgmpro/email-aliases/generate`, { style });
      setLocal(res.data.local);
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/jgmpro/email-aliases`, {
        local: local.trim(),
        ig_username: igUsername.trim() || null,
        notes: notes.trim() || null,
      });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al crear');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-zinc-900 border border-white/10 rounded-2xl p-6 shadow-2xl" data-testid="email-alias-add-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-400" />
            Crear email para IG
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Dirección de email</label>
            <div className="flex items-center gap-0 rounded-lg bg-white/5 border border-white/10 focus-within:border-blue-400/50">
              <input
                type="text"
                required
                value={local}
                onChange={(e) => setLocal(e.target.value.toLowerCase().replace(/[^a-z0-9._+-]/g, ''))}
                placeholder="valentina123"
                data-testid="email-alias-local-input"
                className="flex-1 px-4 py-2.5 bg-transparent text-white placeholder:text-gray-500 focus:outline-none rounded-l-lg"
              />
              <span className="px-3 py-2.5 text-gray-400 border-l border-white/10 text-sm">@jgmodels.com</span>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => handleGenerate('name')}
                disabled={generating}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-xs text-gray-300 disabled:opacity-50"
              >
                <Sparkles className="w-3 h-3" /> Sugerir nombre
              </button>
              <button
                type="button"
                onClick={() => handleGenerate('random')}
                disabled={generating}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-xs text-gray-300 disabled:opacity-50"
              >
                <Sparkles className="w-3 h-3" /> Aleatorio
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">IG username (opcional)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
              <input
                type="text"
                value={igUsername}
                onChange={(e) => setIgUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                placeholder="cuenta_ig_creada"
                data-testid="email-alias-ig-input"
                className="w-full pl-8 pr-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-400/50 text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Para llevar registro de qué email usaste en qué cuenta</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Notas (opcional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Ej: Cuenta principal de Valentina"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-400/50 text-sm resize-none"
            />
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !local.trim()}
            data-testid="email-alias-submit"
            className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Crear email
          </button>
        </form>
      </div>
    </div>
  );
};

export default EmailAliasesPanel;
